import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import { getLoginRequest } from '../auth/msal-config.js';

/**
 * What a signed-out visitor sees instead of a route.
 *
 * WHY THIS EXISTS. Until 25 September a signed-out visitor was let through the
 * gate and shown the route itself. Every hook behind those routes begins
 * `if (!isAuthenticated) return;` and leaves its state at `loading` forever, so
 * what actually rendered was a half-built screen: on /booking, a Where selector
 * permanently reading "Loading…" and a calendar with no hours in it. Nothing on
 * the page said sign in. It looked like the app was broken, and that is how it
 * was reported.
 *
 * There are no public pages. Booking, lessons, messages and the family list are
 * all somebody's account, so there is no route a signed-out visitor can usefully
 * see, and the honest screen is this one.
 *
 * The button is here rather than only in the header. The header's Sign in is a
 * small control in the corner of a phone, and asking somebody to find it is the
 * same failure in a politer form — the action goes where they are already
 * looking.
 *
 * NOT a redirect to Entra on sight. Bouncing a visitor straight to a sign-in
 * page they did not ask for loses the URL they arrived on, and a shared link to
 * an hour is exactly how a parent gets here. The gate renders this in place, so
 * signing in returns them to the route they were headed for.
 *
 * The child's username link says why it asks (ruled 17 October): only the
 * parent who asked may set up a child's sign-in.
 */
export function SignInPrompt() {
  const { instance } = useMsal();
  const { pathname, search } = useLocation();
  // An email's link carries the reader's address (board E1), so Microsoft's
  // sign-in opens with it filled.
  const loginHint = new URLSearchParams(search).get('login_hint') ?? undefined;

  return (
    <section className="panel">
      <p className="empty">
        {pathname === '/athlete-sign-in'
          ? "Sign in to set up your child's sign-in. This is a security step that protects your child's identity: only you can choose their username."
          : 'Booking, lessons and your family’s details are all on your account.'}
      </p>
      <button
        type="button"
        className="primary"
        onClick={() => {
          void instance.loginRedirect(loginHint ? { ...getLoginRequest(), loginHint } : getLoginRequest());
        }}
      >
        Sign in
      </button>
    </section>
  );
}
